import React, { useState } from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import AppLogo from "components/common/AppLogo";
import { AppButton } from "components/misc/Buttons";
import { useAuth } from "contexts/AuthContext";
import { ROUTES } from "constants/routes";
import config from "lib/config";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut", delay: i * 0.1 },
  }),
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const Page = styled.div`
  display: flex;
  min-height: 100vh;
`;

// ── Left branding panel ──

const BrandPanel = styled.div`
  display: none;
  flex-direction: column;
  justify-content: space-between;
  padding: 3rem 3.5rem;
  background: ${C_SECONDARY};
  width: 42%;
  flex-shrink: 0;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const BrandBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem 0;
`;

const BrandEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${C_PRIMARY};
  margin-bottom: 1rem;
`;

const BrandHeading = styled.h2`
  font-size: 2.25rem;
  font-weight: 900;
  color: white;
  line-height: 1.15;
  margin-bottom: 1.5rem;
`;

const BrandDesc = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.75;
  max-width: 26rem;
`;

const StatRow = styled.div`
  display: flex;
  gap: 0;
  border-top: 1px solid #1f1f1f;
  margin-top: 2.5rem;
`;

const StatCell = styled.div`
  flex: 1;
  padding: 1.25rem 0 1.25rem 1.5rem;
  border-right: 1px solid #1f1f1f;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    border-right: none;
  }
`;

const StatVal = styled.p`
  font-size: 1.5rem;
  font-weight: 900;
  color: ${C_PRIMARY};
`;

const StatLabel = styled.p`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 0.2rem;
`;

const BrandFooter = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.2);
`;

// ── Right form panel ──

const FormPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem 1.5rem;
  background: #fafafa;
`;

const FormBox = styled.div`
  width: 100%;
  max-width: 26rem;
`;

const FormEyebrow = styled.p`
  font-size: 0.65rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${C_PRIMARY};
  margin-bottom: 0.6rem;
`;

const FormHeading = styled.h1`
  font-size: 1.75rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.5rem;
`;

const FormSub = styled.p`
  font-size: 0.875rem;
  color: #9ca3af;
  margin-bottom: 2.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #374151;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.9rem 1rem 0.9rem 2.75rem;
  border: 1.5px solid ${(p) => (p.$hasError ? "#ef4444" : "#e5e7eb")};
  background: white;
  font-size: 0.9rem;
  color: ${C_SECONDARY};
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${(p) => (p.$hasError ? "#ef4444" : C_PRIMARY)};
  }

  &::placeholder {
    color: #c1c1c1;
  }
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  padding: 0;

  &:hover {
    color: ${C_SECONDARY};
  }
`;

const ErrorMsg = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  color: #ef4444;
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.75rem;
`;

const ForgotLink = styled(Link)`
  font-size: 0.78rem;
  font-weight: 700;
  color: #9ca3af;
  text-decoration: none;

  &:hover {
    color: ${C_PRIMARY};
  }
`;

// SubmitBtn supprimé — utiliser <AppButton variant="primary" fullWidth>

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  color: #e5e7eb;
  font-size: 0.7rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
`;

const SignupLink = styled.p`
  text-align: center;
  font-size: 0.82rem;
  color: #6b7280;

  a {
    color: ${C_PRIMARY};
    font-weight: 800;
    text-decoration: none;

    &:hover {
      color: ${C_SECONDARY};
    }
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #9ca3af;
  text-decoration: none;
  margin-bottom: 2.5rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &:hover {
    color: ${C_PRIMARY};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [loading, setLoading] = useState(false);

  const validate = () => {
    let ok = true;
    setEmailErr("");
    setPwdErr("");
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailErr("Adresse email invalide.");
      ok = false;
    }
    if (!password || password.length < 6) {
      setPwdErr("Mot de passe requis (min. 6 caractères).");
      ok = false;
    }
    return ok;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await login({ email, password });
      // Récupérer le user depuis localStorage pour lire le rôle
      const raw = localStorage.getItem("momseduc_user");
      const user = raw ? JSON.parse(raw) : null;
      const roles: string[] = user?.roles ?? [];

      if (roles.includes("ROLE_ADMIN") || roles.includes("ROLE_AGENT")) {
        // Redirection externe vers le backoffice EasyAdmin
        window.location.href = `${config.apiBaseUrl.replace("/api", "")}/admin`;
      } else {
        navigate(ROUTES.STUDENT_DASHBOARD);
      }
    } catch {
      setPwdErr("Email ou mot de passe incorrect.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      {/* ── Left branding panel ── */}
      <BrandPanel>
        <AppLogo variant="dark" size="md" href="/" />

        <BrandBody>
          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <BrandEyebrow>Espace étudiant</BrandEyebrow>
            <BrandHeading>Suivez votre dossier en temps réel.</BrandHeading>
            <BrandDesc>
              Accédez à votre espace personnel pour consulter l'état de votre
              procédure, soumettre des documents et dialoguer avec votre
              conseiller.
            </BrandDesc>
          </MDiv>

          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={2}>
            <StatRow>
              {[
                { val: "250+", label: "Étudiants" },
                { val: "94%", label: "Visa obtenu" },
                { val: "48h", label: "Délai AVI" },
              ].map((s) => (
                <StatCell key={s.label}>
                  <StatVal>{s.val}</StatVal>
                  <StatLabel>{s.label}</StatLabel>
                </StatCell>
              ))}
            </StatRow>
          </MDiv>
        </BrandBody>

        <BrandFooter>© {new Date().getFullYear()} MomsEduc Study</BrandFooter>
      </BrandPanel>

      {/* ── Right form panel ── */}
      <FormPanel>
        <FormBox>
          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <BackLink to="/">← Retour au site</BackLink>
          </MDiv>

          <MDiv variants={fadeUp} initial="hidden" animate="visible" custom={1}>
            <FormEyebrow>Connexion</FormEyebrow>
            <FormHeading>Bon retour parmi nous 👋</FormHeading>
            <FormSub>Connectez-vous à votre espace étudiant MomsEduc.</FormSub>
          </MDiv>

          <form onSubmit={handleSubmit} noValidate>
            <MDiv
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
            >
              <FieldGroup>
                <Label htmlFor="email">Adresse email</Label>
                <InputWrapper>
                  <InputIcon>
                    <Mail size={15} />
                  </InputIcon>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jean@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    $hasError={!!emailErr}
                  />
                </InputWrapper>
                {emailErr && <ErrorMsg>{emailErr}</ErrorMsg>}
              </FieldGroup>
            </MDiv>

            <MDiv
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              <FieldGroup>
                <Label htmlFor="password">Mot de passe</Label>
                <InputWrapper>
                  <InputIcon>
                    <Lock size={15} />
                  </InputIcon>
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    $hasError={!!pwdErr}
                  />
                  <ToggleBtn
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Masquer" : "Afficher"}
                  >
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </ToggleBtn>
                </InputWrapper>
                {pwdErr && <ErrorMsg>{pwdErr}</ErrorMsg>}
              </FieldGroup>
            </MDiv>

            <MDiv
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={4}
            >
              <ForgotRow>
                <ForgotLink to="/mot-de-passe-oublie">
                  Mot de passe oublié ?
                </ForgotLink>
              </ForgotRow>

              <AppButton
                type="submit"
                size="lg"
                fullWidth
                loading={loading}
                iconRight={<ArrowRight size={16} />}
                style={{ marginBottom: "1.5rem" }}
              >
                {loading ? "Connexion..." : "Se connecter"}
              </AppButton>

              <Divider>ou</Divider>

              <SignupLink>
                Pas encore de compte ? <Link to="/contact">Contactez-nous</Link>
              </SignupLink>
            </MDiv>
          </form>
        </FormBox>
      </FormPanel>
    </Page>
  );
};

export default LoginPage;
